/**
 * Stock over the next 6 months, drawn the way planning systems show projected inventory, but compact
 * enough for a table row. Every row shares one axis (today → +182 days), with the months labelled in
 * the column header (TimelineHeader), so rows can be compared by eye.
 *   navy   — above safety stock
 *   amber  — below safety stock
 *   red    — shelf empty
 *   ▲      — a delivery lands (PO or container)
 *   dashed — when a new order placed today would arrive (the part's lead time)
 * StockChart is the larger version for the part panel: the stock line itself, with safety stock.
 */
import type { ForecastRow } from "@march/shared";
import { addDays, fmt, projectStock, refLabel, shortDate, todayIso } from "../lib/supply.ts";
import { t } from "../lib/i18n.tsx";

const DAYS = 182;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

type Seg = { from: number; to: number; tone: "ok" | "low" | "empty" };

function segments(stock: number[], safety: number): Seg[] {
  const tone = (v: number): Seg["tone"] => (v <= 0.0001 ? "empty" : v < safety ? "low" : "ok");
  const out: Seg[] = [];
  stock.forEach((v, d) => {
    const t = tone(v);
    const last = out[out.length - 1];
    if (last && last.tone === t) last.to = d + 1;
    else out.push({ from: d, to: d + 1, tone: t });
  });
  return out;
}

/** The stock story in plain words, for anyone who reads the row without a key. */
export function stockStory(
  r: ForecastRow,
  today = todayIso(),
): { text: string; tone: "empty" | "low" | "ok" | "none"; outDate?: string; then?: string } {
  const { stock, rate, safety } = projectStock(r, today, DAYS);
  if (rate <= 0) return { text: t("No use expected"), tone: "none" };
  const firstEmpty = stock.findIndex((v) => v <= 0.0001);
  if (firstEmpty >= 0) {
    const back = stock.findIndex((v, d) => d > firstEmpty && v > 0.0001);
    const outDate = firstEmpty === 0 ? t("Empty now") : shortDate(addDays(today, firstEmpty));
    const start = firstEmpty === 0 ? outDate : t("Runs out {date}", { date: outDate });
    // A delivery after the 6-month window: "back after <the window's last month>" (was always "Apr").
    const end = MONTHS[new Date(addDays(today, DAYS) + "T12:00:00").getMonth()];
    const then = back > 0
      ? t("stock back {date}", { date: shortDate(addDays(today, back)) })
      : (r.arrivals ?? []).length ? t("back after {month}", { month: t(end) }) : t("nothing on order");
    return { text: `${start} · ${then}`, tone: "empty", outDate, then };
  }
  const firstLow = safety > 0 ? stock.findIndex((v) => v < safety) : -1;
  if (firstLow === 0) return { text: t("Below safety stock now"), tone: "low" };
  if (firstLow > 0) return { text: t("Below safety stock from {date}", { date: shortDate(addDays(today, firstLow)) }), tone: "low" };
  return { text: t("In stock all 6 months"), tone: "ok" };
}

/**
 * One row's next 6 months: the story in words, with a thin bar under it showing the same thing
 * (red = empty, amber = below safety stock, navy = fine) on the axis shared by every row.
 */
export function StockTimeline({ r, width = 180 }: { r: ForecastRow; unit?: string; width?: number }) {
  const today = todayIso();
  const story = stockStory(r, today);
  if (story.tone === "none") return <span className="tl-none">{story.text}</span>;
  const { stock, safety } = projectStock(r, today, DAYS);
  const x = (d: number) => (d / DAYS) * width;
  return (
    <span className={`tl-cell ${story.tone}`}>
      <span className="tl-text">{story.text}</span>
      <svg className="tl" width={width} height={6} viewBox={`0 0 ${width} 6`} aria-hidden="true">
        {segments(stock, safety).map((sg, i) => (
          <rect key={i} x={x(sg.from)} y={0} width={Math.max(1, x(sg.to) - x(sg.from))} height={6} className={`tl-${sg.tone}`} />
        ))}
      </svg>
    </span>
  );
}

/** Month labels for the timeline column header, on the same axis as the rows. */
export function TimelineHeader({ width = 180 }: { width?: number }) {
  const today = new Date(todayIso() + "T12:00:00");
  const ticks: { x: number; label: string }[] = [];
  for (let m = 1; m <= 6; m++) {
    const d = new Date(today.getFullYear(), today.getMonth() + m, 1, 12);
    const day = Math.round((d.getTime() - today.getTime()) / 86400000);
    // keep clear of the "Today" label at the left edge
    if (day < DAYS && (day / DAYS) * width > 34) ticks.push({ x: (day / DAYS) * width, label: MONTHS[d.getMonth()] });
  }
  return (
    <span className="tl-head" style={{ width }}>
      <span className="tl-head-t">Stock, next 6 months</span>
      <span className="tl-head-m">
        <span style={{ left: 0 }}>Today</span>
        {ticks.map((t) => <span key={t.label} style={{ left: t.x }}>{t.label}</span>)}
      </span>
    </span>
  );
}

export function TimelineKey() {
  return (
    <span className="tl-key">
      <span><i className="k ok" />above safety stock</span>
      <span><i className="k low" />below safety stock</span>
      <span><i className="k empty" />empty</span>
      <span><svg width="10" height="8"><path d="M5 0 l-4 7 h8 z" className="tl-arr" /></svg>delivery lands</span>
      <span><i className="k lead" />a new order would arrive</span>
    </span>
  );
}

/** Part panel: projected stock as a line, with safety stock, empty periods and labelled deliveries. */
export function StockChart({ r, unit }: { r: ForecastRow; unit: string }) {
  const today = todayIso();
  const { stock, rate, safety } = projectStock(r, today, DAYS);
  if (rate <= 0) return <div className="muted">No use expected, so no projection.</div>;
  const W = 400, H = 170, pl = 40, pr = 8, pt = 10, pb = 22;
  const max = Math.max(...stock, safety, 1) * 1.1;
  const X = (d: number) => pl + (d / DAYS) * (W - pl - pr);
  const Y = (v: number) => pt + (1 - v / max) * (H - pt - pb);
  const line = stock.map((v, d) => `${d ? "L" : "M"}${X(d).toFixed(1)},${Y(v).toFixed(1)}`).join(" ");
  const segs = segments(stock, safety).filter((s) => s.tone === "empty");
  const arrivals = (r.arrivals ?? []).filter((a) => a.date).map((a) => ({ ...a, day: Math.max(0, Math.round((new Date(a.date + "T12:00:00").getTime() - new Date(today + "T12:00:00").getTime()) / 86400000)) })).filter((a) => a.day < DAYS);
  const lead = r.lead_days ?? 0;
  const t0 = new Date(today + "T12:00:00");
  const months = [1, 2, 3, 4, 5, 6].map((m) => {
    const d = new Date(t0.getFullYear(), t0.getMonth() + m, 1, 12);
    return { day: Math.round((d.getTime() - t0.getTime()) / 86400000), label: MONTHS[d.getMonth()] };
  }).filter((m) => m.day < DAYS && X(m.day) - pl > 30 && W - pr - X(m.day) > 12);
  return (
    <div>
    <svg className="sc" viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Projected stock, next 6 months">
      {segs.map((s, i) => <rect key={i} x={X(s.from)} y={pt} width={X(s.to) - X(s.from)} height={H - pt - pb} className="sc-empty" />)}
      <line x1={pl} x2={W - pr} y1={Y(0)} y2={Y(0)} className="sc-axis" />
      {months.map((m) => (
        <g key={m.label}>
          <line x1={X(m.day)} x2={X(m.day)} y1={pt} y2={H - pb} className="sc-grid" />
          <text x={X(m.day)} y={H - 6} textAnchor="middle" className="sc-t">{m.label}</text>
        </g>
      ))}
      <text x={pl - 6} y={Y(max / 1.1) + 4} textAnchor="end" className="sc-t">{fmt(max / 1.1)}</text>
      <text x={pl - 6} y={Y(0) + 4} textAnchor="end" className="sc-t">0</text>
      {safety > 0 && <line x1={pl} x2={W - pr} y1={Y(safety)} y2={Y(safety)} className="sc-safety" />}
      {lead > 0 && lead < DAYS && <line x1={X(lead)} x2={X(lead)} y1={pt} y2={H - pb} className="sc-lead" />}
      <path d={line} className="sc-line" />
      {arrivals.map((a, i) => (
        <g key={i}>
          <path d={`M${X(a.day)} ${H - pb} l-5 7 h10 z`} className={a.date! < today ? "tl-arr late" : "tl-arr"} />
          <title>{`${refLabel(a.ref, a.source)}: ${fmt(a.qty)} ${unit}, ${a.date! < today ? "overdue (was due " + shortDate(a.date) + ")" : shortDate(a.date)}`}</title>
        </g>
      ))}
      <text x={pl} y={H - 6} textAnchor="start" className="sc-t">Today</text>
    </svg>
    <div className="sc-key">
      <span><i className="k line" />stock</span>
      {safety > 0 && <span><i className="k safety" />safety stock {fmt(safety)}</span>}
      {lead > 0 && lead < DAYS && <span><i className="k lead" />an order placed today would land {shortDate(addDays(today, lead))}</span>}
      {segs.length > 0 && <span><i className="k empty" />empty</span>}
      {arrivals.length > 0 && <span><svg width="10" height="8"><path d="M5 0 l-4 7 h8 z" className="tl-arr" /></svg>delivery</span>}
    </div>
    </div>
  );
}

/**
 * Forecast row: three bars on one scale, stacked: what it will use in the next 90 days, what's on
 * the shelf, and what's on order. If the bottom two together are shorter than the top one, it's short.
 */
export function ForecastBars({ r }: { r: ForecastRow; unit?: string }) {
  const use = Math.max(0, r.demand?.forecasted ?? 0);
  const onHand = Math.max(0, r.supply.comp_available ?? 0);
  const onOrder = Math.max(0, (r.supply.incoming_within_lead ?? 0) + (r.supply.incoming_after_lead ?? 0) || (r.supply.comp_po ?? 0));
  const max = Math.max(use, onHand, onOrder, 1);
  const W = 150;
  const bar = (v: number, cls: string) => <span className={`fb-bar ${cls}`} style={{ width: v > 0 ? Math.max(2, (v / max) * W) : 0 }} />;
  const n = (v: number) => (v > 0 && v < 1 ? "<1" : fmt(v));
  return (
    <span className="fb">
      <span className="fb-l">Forecast use</span>{bar(use, "use")}<span className="fb-n">{n(use)}</span>
      <span className="fb-l">Current stock</span>{bar(onHand, "stock")}<span className={`fb-n${onHand === 0 ? " zero" : ""}`}>{n(onHand)}</span>
      <span className="fb-l">On order</span>{bar(onOrder, "order")}<span className="fb-n">{onOrder ? n(onOrder) : "—"}</span>
    </span>
  );
}

/** The three numbers the forecast row compares, on one scale for that row. */
export function forecastTriple(r: ForecastRow) {
  const use = Math.max(0, r.demand?.forecasted ?? 0);
  const onHand = Math.max(0, r.supply.comp_available ?? 0);
  const onOrder = Math.max(0, (r.supply.incoming_within_lead ?? 0) + (r.supply.incoming_after_lead ?? 0) || (r.supply.comp_po ?? 0));
  // One true scale per row: use, stock and each single delivery (on order is drawn one bar per delivery).
  const biggestDelivery = Math.max(0, ...deliveryBuckets(r).slice(0, 4).map((b) => b.qty));
  return { use, onHand, onOrder, max: Math.max(use, onHand, biggestDelivery, 1) };
}

/** One upright bar with its number on top, for a table cell. Height is relative to the row's scale. */
export function VBar({ v, max, kind, sub }: { v: number; max: number; kind: "use" | "stock" | "order" | "late"; sub?: string }) {
  const H = 40;
  const h = v > 0 ? Math.max(3, Math.min(1, v / max) * H) : 0;
  const label = v > 0 && v < 1 ? "<1" : fmt(v);
  return (
    <span className="vb">
      <span className="vb-track">
        <span className={`vb-n${v === 0 && kind === "stock" ? " zero" : ""}`}>{label}</span>
        <span className={`vb-bar ${kind}`} style={{ height: h }} />
      </span>
      {/* always reserve the label line so every bar in the row stands on the same floor */}
      <span className="vb-sub">{sub ?? " "}</span>
    </span>
  );
}

/** On order as one bar per delivery (soonest first), each with its quantity on top and its date under. */
export function OrderBars({ r, max }: { r: ForecastRow; max: number }) {
  const today = todayIso();
  const buckets = deliveryBuckets(r, today);
  if (!buckets.length) return <span className="vb-none">—</span>;
  const shown = buckets.slice(0, 4);
  return (
    <span className="vbs">
      {shown.map((b) => (
        <VBar key={b.key} v={b.qty} max={max} kind={b.key === "late" ? "late" : "order"} sub={b.label} />
      ))}
      {buckets.length > 4 && <span className="vb-more">+{buckets.length - 4}</span>}
    </span>
  );
}

/**
 * On-order quantities by when they land: everything overdue is one "late" bar, then one bar per date
 * (POs and containers landing the same day are added together), then anything without a date.
 */
export function deliveryBuckets(r: ForecastRow, today = todayIso()) {
  const m = new Map<string, { key: string; qty: number; label: string; sort: string; refs: Set<string> }>();
  for (const a of r.arrivals ?? []) {
    if (a.qty <= 0) continue;
    const late = a.date != null && a.date < today;
    const key = late ? "late" : a.date ?? "nodate";
    const label = late ? t("late") : a.date ? shortDate(a.date).replace(/ (\d{2})(\d{2})$/, " '$2") : t("no date");
    const sort = late ? "0" : a.date ?? "9999";
    const cur = m.get(key) ?? { key, qty: 0, label, sort, refs: new Set<string>() };
    cur.qty += a.qty;
    if (a.ref) cur.refs.add(a.source === "container" ? a.ref : refLabel(a.ref, "po"));
    m.set(key, cur);
  }
  // The late bucket says which delivery to chase: its PO (or container), or how many there are.
  const late = m.get("late");
  if (late) late.label = late.refs.size === 1 ? `${t("late")} · ${[...late.refs][0]}` : late.refs.size > 1 ? `${t("late")} · ${t("{n} POs", { n: late.refs.size })}` : t("late");
  return [...m.values()].sort((x, y) => x.sort.localeCompare(y.sort));
}

/* ---- Numbers first, a thin bar under each (chosen 21 Oct 2026) ----
 * Every bar in a row shares one scale: the larger of current stock and use.
 *   stock     solid blue
 *   forecast  light blue up to what stock covers, red for the rest (what's missing)
 *   on order  striped light blue (not here yet); overdue deliveries striped amber
 * A bar longer than the scale fills its track; the number above always says the real amount.
 */
type BarSeg = { v: number; cls: string };

function NumBar({ label, segs, max, zero, title }: { label: string; segs: BarSeg[]; max: number; zero?: boolean; title?: string }) {
  let left = 1;
  const parts = segs.map((s) => {
    const w = Math.min(left, Math.max(0, s.v) / max);
    left -= w;
    return { w: s.v > 0 ? Math.max(w, 0.02) : 0, cls: s.cls };
  });
  return (
    <span className="nb" title={title}>
      <span className={`nb-n${zero ? " zero" : ""}`}>{label}</span>
      <span className="nb-track">{parts.map((p, i) => p.w > 0 && <i key={i} className={p.cls} style={{ width: `${p.w * 100}%` }} />)}</span>
    </span>
  );
}

const nbLabel = (v: number) => (v > 0 && v < 1 ? "<1" : fmt(v));

export function StockNum({ v, max }: { v: number; max: number }) {
  return <NumBar label={nbLabel(v)} segs={[{ v, cls: "stock" }]} max={max} zero={v <= 0} />;
}

/** Forecast use: the part current stock doesn't cover in red (first, so it grows from the left), then the covered part in light blue. */
export function UseNum({ use, stock, max }: { use: number; stock: number; max: number }) {
  const covered = Math.min(use, Math.max(0, stock));
  const missing = Math.max(0, use - covered);
  return (
    <NumBar label={nbLabel(use)} max={max} segs={[{ v: missing, cls: "miss" }, { v: covered, cls: "est" }]}
      title={missing > 0 ? t("{n} more than current stock", { n: fmt(missing) }) : t("Current stock covers it")} />
  );
}

/** Actual use (Monitor's last 30 days): plain grey, it already happened. */
export function UsedNum({ v, max }: { v: number; max: number }) {
  return <NumBar label={nbLabel(v)} segs={[{ v, cls: "used" }]} max={max} />;
}

/** On order: each delivery as "qty date" (late ones marked), one striped bar for the total. */
export function OrderNum({ r, max }: { r: ForecastRow; max: number }) {
  const buckets = deliveryBuckets(r);
  if (!buckets.length) return <span className="vb-none">—</span>;
  const late = buckets.filter((b) => b.key === "late").reduce((t, b) => t + b.qty, 0);
  const rest = buckets.filter((b) => b.key !== "late").reduce((t, b) => t + b.qty, 0);
  const shown = buckets.slice(0, 2);
  return (
    <span className="nb">
      {shown.map((b) => (
        <span key={b.key} className="nb-o"><b>{fmt(b.qty)}</b> <span className={b.key === "late" ? "warn" : ""}>{b.label}</span></span>
      ))}
      {buckets.length > 2 && <span className="nb-o"><span>{t("+{n} more", { n: buckets.length - 2 })}</span></span>}
      <span className="nb-track">
        {[{ v: late, cls: "late" }, { v: rest, cls: "order" }].map((s, i) => {
          const w = Math.min(1, s.v / max);
          return s.v > 0 && <i key={i} className={s.cls} style={{ width: `${Math.max(w, 0.02) * 100}%` }} />;
        })}
      </span>
    </span>
  );
}

/** On order without dates (no forecast row, e.g. Gold Crest): one total. */
export function OrderTotal({ v, max }: { v: number; max: number }) {
  if (v <= 0) return <span className="vb-none">—</span>;
  return (
    <span className="nb">
      <span className="nb-o"><b>{fmt(v)}</b> <span>{t("no date")}</span></span>
      <span className="nb-track"><i className="order" style={{ width: `${Math.max(0.02, Math.min(1, v / max)) * 100}%` }} /></span>
    </span>
  );
}
